import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, GlobalStyles } from '@mui/material';
import UnitReportDocument from './UnitReportDocument';

export const UNIT_REPORT_PRINT_CLASS = 'meta-unit-report-print-root';

/**
 * Vista imprimible del informe («Imprimir o guardar como PDF»): el mismo JSON, en un contenedor que
 * solo existe mientras se imprime (portal al `body`). En pantalla está oculto; al imprimir, todo lo
 * demás del `body` se oculta (la app privada, los diálogos) y queda solo el documento en A4.
 *
 * @returns {{ print: () => void, printNode: React.ReactNode }}
 */
export function useUnitReportPrint(report) {
  const [printing, setPrinting] = useState(false);
  const print = useCallback(() => { if (report) setPrinting(true); }, [report]);

  useEffect(() => {
    if (!printing) return undefined;
    // Un frame para que el portal pinte antes de abrir el diálogo de impresión.
    const id = window.requestAnimationFrame(() => {
      try {
        if (typeof window.print === 'function') window.print();
      } finally {
        setPrinting(false);
      }
    });
    return () => window.cancelAnimationFrame(id);
  }, [printing]);

  const printNode = printing && report ? <UnitReportPrintPortal report={report} /> : null;
  return { print, printing, printNode };
}

export const unitReportPrintStyles = (
  <GlobalStyles
    styles={{
      '@page': { size: 'A4', margin: '14mm' },
      '@media print': {
        [`body > *:not(.${UNIT_REPORT_PRINT_CLASS})`]: { display: 'none !important' },
        [`.${UNIT_REPORT_PRINT_CLASS}`]: { display: 'block !important' },
        body: { background: '#FFFFFF !important' },
      },
    }}
  />
);

export function UnitReportPrintPortal({ report }) {
  if (typeof document === 'undefined') return null;
  return createPortal(
    <Box className={UNIT_REPORT_PRINT_CLASS} data-testid="unit-report-print" sx={{ display: 'none', bgcolor: '#FFFFFF', color: '#1d2327', p: 0 }}>
      {unitReportPrintStyles}
      <UnitReportDocument report={report} tone="paper" />
    </Box>,
    document.body,
  );
}
